import type { EntityScopeState } from '@/slice/entityScopeSlice';

/**
 * DV-3345: the `{entity}` segment of property-service's
 * `/properties/pms/{entity}/…` routes. An entityId (DV-3382), or, only where
 * the SESSION carries no id, its name (see `sessionPmsAccountFrom`).
 *
 * Branded so a raw string cannot be passed by accident: obtain one from
 * `useSessionPmsAccount()` (the /home pages) or `propertyPmsAccount()` (the
 * Global Orders photos page). The PMS layer never reads the session itself.
 */
export type PmsAccountRef = string & { readonly __brand: 'PmsAccountRef' };

/** The property's own account. Id only; null means no PMS surface. */
export const propertyPmsAccount = (entityUUID: string | null | undefined): PmsAccountRef | null =>
  entityUUID ? (entityUUID as PmsAccountRef) : null;

/**
 * The session's account, or null when no account is chosen.
 *
 * `status` is checked first: un-choosing an account (`scopeCleared`, which the
 * internal route's UnscopedConsole dispatches) leaves the previous
 * `entityId` in place, and reading it would name the account the editor was
 * last in.
 *
 * The name fallback (DV-3382) stays for a scoped session hydrated without
 * `selectedEntity`: `readPersistedScope` marks a session scoped from
 * `selectedOrg` alone, so an old localStorage can get here (spec D14).
 *
 * Only `useSessionPmsAccount` calls this; the lint rule (spec D4) keeps the
 * rest of the PMS layer off the session.
 */
export const sessionPmsAccountFrom = (
  scope: Pick<EntityScopeState, 'status' | 'entityId' | 'entityName'>
): PmsAccountRef | null => {
  if (scope.status !== 'scoped') return null;
  const ref = scope.entityId ?? scope.entityName;
  return ref ? (ref as PmsAccountRef) : null;
};
